import React, { useRef, useState } from 'react';

import Avatar from '@liquidweb-dev/undercurrent/Avatar';
import IconButton from '@liquidweb-dev/undercurrent/IconButton';
import InputAdornment from '@liquidweb-dev/undercurrent/InputAdornment';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import CirclePlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/CirclePlus';
import { useInput } from 'components/LWForm/LWForm';
import XIcon from '@liquidweb-dev/undercurrent/icons/lucide/X';

/**
 * A text input with multiple values
 * @param {import('@liquidweb-dev/undercurrent/TextField').TextFieldProps} props
 */
const HookedTextFieldList = ({
	helperText,
	label,
	name,
	placeholder,
	error: errorProp,
	select,
	required,
	...rest
}) => {
	const [inputValue, setInputValue] = useState('');
	const [inputError, setInputError] = useState('');
	const { value, validate, onValueChange } = useInput(name);

	const inputRef = useRef(null);

	if (!Array.isArray(value)) {
		throw new Error('HookedTextFieldList: value must be an array');
	}

	if (!name) {
		throw new Error('HookedTextFieldList: name is required');
	}

	const handleChange = ({ target }) => {
		if (inputError) {
			setInputError(validate(target));
		}

		setInputValue(target.value);
	};

	const handleAddChip = () => {
		const newError = validate(inputRef.current);
		if (newError) {
			setInputError(newError);
			return;
		}

		const newItem = inputRef.current?.value;

		if (!newItem || value.includes(newItem)) {
			return;
		}

		onValueChange([...value, newItem]);
		setInputValue('');
	};

	const handleRemoveItem = (itemToRemove) => {
		onValueChange(value.filter((item) => item !== itemToRemove));
	};

	const handleKeyDown = (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();

			handleAddChip();
		}
	};

	return (
		<Stack spacing={150}>
			<TextField
				helperText={inputError || errorProp || helperText}
				label={label}
				name={name}
				placeholder={placeholder || label}
				error={Boolean(inputError || errorProp)}
				select={select}
				required={required}
				onKeyDown={handleKeyDown}
				slotProps={{
					input: {
						endAdornment: (
							<InputAdornment position="end" sx={{ cursor: 'pointer' }}>
								<CirclePlusIcon onClick={handleAddChip} />
							</InputAdornment>
						),
					},
					htmlInput: {
						className: 'input-HookedTextFieldList',
					},
				}}
				inputRef={inputRef}
				{...rest}
				value={inputValue}
				onChange={handleChange}
			/>

			{value.length > 0 && (
				<Stack spacing={100}>
					{value.map((item) => (
						<Stack
							key={item}
							direction="row"
							gap={100}
							alignItems="center"
							justifyContent="space-between"
							paddingY={100}
						>
							<Stack direction="row" spacing={150}>
								<Avatar size="small">{item.charAt(0).toUpperCase()}</Avatar>
								<TypographyOverflow variant="body-md" fontWeight={500}>
									{item}
								</TypographyOverflow>
							</Stack>
							<IconButton
								size="small"
								variant="ghost"
								onClick={() => handleRemoveItem(item)}
							>
								<XIcon />
							</IconButton>
						</Stack>
					))}
				</Stack>
			)}
		</Stack>
	);
};

export default HookedTextFieldList;
